import { useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import {
  AccountingSyncBadge,
  DocumentAccountingSyncCard,
  DocumentAttachmentBadge,
  platformName,
} from "@/components/AccountingSync";
import {
  accountLabel,
  taxRateLabel,
  type LedgerAccount,
  type PlatformTaxRate,
} from "@/components/AccountingSettingsSections";
import { InfoHint } from "@/components/InfoHint";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";

type BillResponse = components["schemas"]["BillResponse"];
type BillLineItem = components["schemas"]["BillLineItemResponse"];

const DEFAULT = "__default__";
export const AUTOMATIC_TAX = "Automatic";
const AUTOMATIC_TAX_HINT = "Dolfin picks the purchase rate that charges the VAT printed on the line.";

// A bill reaches the books once approved; before that the server refuses a push.
const PUSHABLE_STATES = new Set(["Approved", "Scheduled", "PaymentInProgress", "PaymentFailed", "PartiallyPaid", "Paid"]);
// Coding is accounting metadata, not bill content, so it can be set from review onwards, approval included,
// until the bill is in the platform.
const CODABLE_STATES = new Set(["PendingReview", "NeedsApproval", ...PUSHABLE_STATES]);

export function billCodingVisible(bill: BillResponse) {
  return CODABLE_STATES.has(bill.state ?? "");
}

// Shown once approved, or earlier if the bill somehow already has a sync.
export function BillAccountingCard({
  bill,
  titleClassName,
  onRefresh,
}: {
  bill: BillResponse;
  titleClassName?: string;
  onRefresh: () => Promise<void>;
}) {
  const api = useDolfinClient();
  const pushable = PUSHABLE_STATES.has(bill.state ?? "");
  if (!pushable && !bill.accountingSync) return null;
  const sync = bill.accountingSync;

  return (
    <DocumentAccountingSyncCard
      sync={sync}
      canPush={pushable}
      push={() => api.POST("/v1/bills/{id}/accounting/push", { params: { path: { id: bill.id! } } })}
      failedMessage="Couldn't push the bill to your accounting software."
      hint="Approved bills are pushed to your accounting software with their PDF once a default expense account is chosen in Organisation, Accounting. Payments and supplier credit follow the bill there. Nothing already in your books is edited."
      notYet="Not pushed yet. Bills approved before your accounting software was connected are pushed by hand."
      synced={`In ${platformName(sync?.platform)}. Payments and supplier credit recorded here follow it automatically.`}
      titleClassName={titleClassName}
      onRefresh={onRefresh}
    >
      {bill.paymentAccountingSync || bill.documentAccountingSync ? (
        <dl className="space-y-2 border-t pt-3">
          {bill.paymentAccountingSync ? (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-muted-foreground">Payment</dt>
              <dd>
                <AccountingSyncBadge sync={bill.paymentAccountingSync} waitsFor="bill" />
              </dd>
            </div>
          ) : null}
          {bill.documentAccountingSync ? (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-muted-foreground">Document</dt>
              <dd>
                <DocumentAttachmentBadge sync={bill.documentAccountingSync} />
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </DocumentAccountingSyncCard>
  );
}

// Read-only once the bill is in the platform: a change would no longer reach it.
export function BillLineCoding({
  bill,
  line,
  accounts,
  taxRates,
  defaultAccountLabel,
  onSaved,
}: {
  bill: BillResponse;
  line: BillLineItem;
  accounts: LedgerAccount[];
  taxRates: PlatformTaxRate[];
  /** What a line with no account of its own posts to, e.g. "Supplier default: 429 · General Expenses". */
  defaultAccountLabel: string;
  onSaved: (bill: BillResponse) => void;
}) {
  const api = useDolfinClient();
  const [saving, setSaving] = useState(false);
  const locked = bill.accountingSync?.status === "Synced";

  async function save(ledgerAccountId: string | null, accountingTaxRateId: string | null) {
    setSaving(true);
    // Both overrides are replaced for each listed line, so the one not being changed is sent as it stands.
    const { data, error } = await api.PATCH("/v1/bills/{id}/accounting/coding", {
      params: { path: { id: bill.id! } },
      body: { lines: [{ lineId: line.id!, ledgerAccountId, accountingTaxRateId }] },
    });
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't save where this line goes in your books."));
    } else {
      onSaved(data);
    }
    setSaving(false);
  }

  const account = line.ledgerAccountId ? accounts.find((a) => a.id === line.ledgerAccountId) : null;
  const rate = line.accountingTaxRateId ? taxRates.find((r) => r.id === line.accountingTaxRateId) : null;

  if (locked) {
    return (
      <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs @sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-muted-foreground">Account</dt>
          <dd className="font-medium text-pretty">
            {line.ledgerAccountId ? (account ? accountLabel(account) : "An archived account") : defaultAccountLabel}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="flex items-center gap-1 text-muted-foreground">
            Tax rate
            {line.accountingTaxRateId ? null : <InfoHint label="How the tax rate is chosen">{AUTOMATIC_TAX_HINT}</InfoHint>}
          </dt>
          <dd className="font-medium text-pretty">
            {line.accountingTaxRateId ? (rate ? taxRateLabel(rate) : "An archived rate") : AUTOMATIC_TAX}
          </dd>
        </div>
      </dl>
    );
  }

  return (
    <div className="mt-3 grid items-end gap-2 @sm:grid-cols-2">
      <div className="min-w-0 space-y-1">
        <span className="text-xs text-muted-foreground">Account</span>
        <Select
          value={line.ledgerAccountId ?? DEFAULT}
          disabled={saving}
          onValueChange={(v) => save(v === DEFAULT ? null : v, line.accountingTaxRateId ?? null)}
        >
          <SelectTrigger size="sm" className="w-full" aria-label={`Account for ${line.description ?? "this line"}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT}>{defaultAccountLabel}</SelectItem>
            {/* An override that has since been archived still has to show as the current value. */}
            {line.ledgerAccountId && !account ? (
              <SelectItem value={line.ledgerAccountId}>An archived account</SelectItem>
            ) : null}
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id!}>
                {accountLabel(a)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-0 space-y-1">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Tax rate
            <InfoHint label="How the tax rate is chosen">{AUTOMATIC_TAX_HINT}</InfoHint>
            {saving ? <Spinner className="size-3 shrink-0" /> : null}
          </span>
          <Select
            value={line.accountingTaxRateId ?? DEFAULT}
            disabled={saving}
            onValueChange={(v) => save(line.ledgerAccountId ?? null, v === DEFAULT ? null : v)}
          >
            <SelectTrigger size="sm" className="w-full" aria-label={`Tax rate for ${line.description ?? "this line"}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT}>{AUTOMATIC_TAX}</SelectItem>
              {line.accountingTaxRateId && !rate ? (
                <SelectItem value={line.accountingTaxRateId}>An archived rate</SelectItem>
              ) : null}
              {taxRates.map((r) => (
                <SelectItem key={r.id} value={r.id!}>
                  {taxRateLabel(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
      </div>
    </div>
  );
}
